// src/Pages/AdminPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Music, Image, Trash2, Plus, Save, Eye, EyeOff, Mail, Calendar, Users as UsersIcon, DollarSign, MessageSquare, RefreshCw, Send } from 'lucide-react';
import * as AdminAPI from '@/lib/admin-api';
import AdminPaymentSettings from '@/components/AdminPaymentSettings';
import { supabase } from '@/lib/supabase';

interface Song {
  id: string;
  title: string;
  artist: string;
  url: string;
  cover: string;
}

interface Ad {
  id: string;
  title: string;
  description: string;
  image: string;
  link: string;
  active: boolean;
}

interface UserInfo {
  id: string;
  email: string;
  name: string;
  created_at: string;
  last_active_at?: string;
  student_organizer_paid?: boolean;
  personal_organizer_paid?: boolean;
  organizer_mode?: 'student' | 'personal';
  role?: 'super' | 'admin' | 'user';
}

interface ActiveSession {
  roomId: string;
  roomTitle?: string;
  startedAt: number;
  lastSeen: number;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'music' | 'ads' | 'users' | 'payment' | 'resources' | 'suggestions' | 'sessions' | 'broadcast'>('music');
  const [loading, setLoading] = useState(false);

  // Admin password (in production, use proper authentication)
  const ADMIN_PASSWORD = 'whipt2026admin';

  // Music state
  const [songs, setSongs] = useState<Song[]>([]);
  const [editingSong, setEditingSong] = useState<Song | null>(null);
  const [audioInputType, setAudioInputType] = useState<'url' | 'embed'>('url');

  // Ads state
  const [ads, setAds] = useState<Ad[]>([]);
  const [editingAd, setEditingAd] = useState<Ad | null>(null);

  // Users state
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [userCount, setUserCount] = useState(0);
  
  // Advertisement global toggle
  const [adsEnabled, setAdsEnabled] = useState(() => {
    const saved = localStorage.getItem('whipt_ads_enabled');
    return saved !== 'false'; // Default to true if not set
  });

  // Success message
  const [successMessage, setSuccessMessage] = useState('');

  // Suggestions state
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<any | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Session monitor state (client-side heartbeat)
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const sessionRealtimeRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Check if already authenticated
  useEffect(() => {
    const adminAuth = sessionStorage.getItem('whipt_admin_auth');
    if (adminAuth === 'true') {
      setIsAuthenticated(true);
      return;
    }

    // Dev bypass: allow admin access when dev mode is enabled
    if (localStorage.getItem('whipt_dev_mode') === 'true') {
      setIsAuthenticated(true);
      sessionStorage.setItem('whipt_admin_auth', 'true');
    }
  }, []);

  // Poll active sessions from client heartbeats (same-browser/same-origin)
  useEffect(() => {
    const loadSessions = () => {
      const data: ActiveSession[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith('whipt_active_session_')) continue;
        try {
          const parsed = JSON.parse(localStorage.getItem(key) || '{}');
          if (parsed.roomId && parsed.startedAt && parsed.lastSeen) {
            data.push(parsed);
          }
        } catch (e) {
          console.warn('Bad session payload', key, e);
        }
      }
      setSessions(data.sort((a, b) => b.lastSeen - a.lastSeen));
    };

    loadSessions();
    const interval = setInterval(loadSessions, 5000);
    const handleStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key.startsWith('whipt_active_session_') || e.key.startsWith('whipt_admin_terminate')) {
        loadSessions();
      }
    };

    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel('whipt_sessions');
      channel.addEventListener('message', (event) => {
        if (!event?.data) return;
        if (event.data.type === 'heartbeat' || event.data.type === 'ended') {
          loadSessions();
        }
      });
    }

    if (!sessionRealtimeRef.current) {
      sessionRealtimeRef.current = supabase.channel('whipt-sessions');
      sessionRealtimeRef.current
        .subscribe()
        .on('broadcast', { event: 'heartbeat' }, () => loadSessions())
        .on('broadcast', { event: 'ended' }, () => loadSessions());
    }

    window.addEventListener('storage', handleStorage);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorage);
      channel?.close();
      if (sessionRealtimeRef.current) {
        sessionRealtimeRef.current.unsubscribe();
        sessionRealtimeRef.current = null;
      }
    };
  }, []);

  // Load data
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load songs from database
      const songsData = await AdminAPI.getAllSongs();
      setSongs(songsData);

      const adsData = await AdminAPI.getAllAds();
      if (adsData.length > 0) {
        setAds(
          adsData.map((ad) => ({
            id: ad.id,
            title: ad.title,
            description: ad.description,
            image: ad.image,
            link: ad.link,
            active: ad.active === 1
          }))
        );
      } else {
        setAds([]);
      }

      const adsSetting = await AdminAPI.getAppSetting('ads_enabled');
      if (adsSetting !== null) {
        setAdsEnabled(adsSetting !== 'false');
      } else {
        setAdsEnabled(true);
      }

      // Load users from database
      const usersData = await AdminAPI.getAllUsers();
      
      // Enhance users with subscription data (Note: In production, this should come from backend)
      const enhancedUsers = usersData.map(user => {
        // For now, we'll show that subscription data needs backend integration
        // In a real app, subscription status would be stored in the database
        return {
          ...user,
          student_organizer_paid: false, // TODO: Get from backend/Stripe
          personal_organizer_paid: false, // TODO: Get from backend/Stripe
          organizer_mode: undefined as 'student' | 'personal' | undefined // TODO: Get from user preferences
        };
      });
      
      setUsers(enhancedUsers);
      
      // Get user count
      const count = await AdminAPI.getUserCount();
      setUserCount(count);

      // Load suggestions from database
      await loadSuggestions();
    } catch (error) {
      console.error('Failed to load data:', error);
      showSuccess('Failed to load data from database');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (ms: number) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const terminateSession = (roomId: string) => {
    localStorage.setItem(`whipt_admin_terminate_room_${roomId}`, Date.now().toString());
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel('whipt_sessions');
      ch.postMessage({ type: 'terminate_room', roomId });
      ch.close();
    }
    sessionRealtimeRef.current?.send({ type: 'broadcast', event: 'terminate', payload: { roomId } });
    localStorage.removeItem(`whipt_active_session_${roomId}`);
    setSuccessMessage(`Terminated session ${roomId}`);
    setTimeout(() => setSuccessMessage(''), 2500);
  };

  const terminateAllSessions = () => {
    localStorage.setItem('whipt_admin_terminate_all', Date.now().toString());
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel('whipt_sessions');
      ch.postMessage({ type: 'terminate_all' });
      ch.close();
    }
    sessionRealtimeRef.current?.send({ type: 'broadcast', event: 'terminate', payload: { scope: 'all' } });
    sessions.forEach((s) => localStorage.removeItem(`whipt_active_session_${s.roomId}`));
    setSuccessMessage('Terminated all sessions');
    setTimeout(() => setSuccessMessage(''), 2500);
  };

  const loadSuggestions = async () => {
    try {
      setSuggestionsLoading(true);
      const { Database } = await import('@/database/Database');
      const db = new Database();
      
      const results = await db.query(
        'SELECT * FROM entities WHERE entity_type = ? ORDER BY created_at DESC',
        ['Suggestion']
      );
      
      const formattedSuggestions = results.map((row: any) => ({
        id: row.id,
        ...JSON.parse(row.data),
        timestamp: row.created_at,
      }));
      
      setSuggestions(formattedSuggestions);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const markSuggestionAsRead = async (id: string) => {
    try {
      const { Database } = await import('@/database/Database');
      const db = new Database();
      
      const suggestion = suggestions.find(s => s.id === id);
      if (suggestion) {
        suggestion.read = true;
        await db.query(
          'UPDATE entities SET data = ?, updated_at = ? WHERE id = ?',
          [JSON.stringify(suggestion), new Date().toISOString(), id]
        );
        
        setSuggestions(prev =>
          prev.map(s => s.id === id ? { ...s, read: true } : s)
        );
      }
    } catch (error) {
      console.error('Error marking suggestion as read:', error);
    }
  };

  const deleteSuggestion = async (id: string) => {
    if (!confirm('Are you sure you want to delete this suggestion?')) {
      return;
    }

    try {
      const { Database } = await import('@/database/Database');
      const db = new Database();
      
      await db.query('DELETE FROM entities WHERE id = ?', [id]);
      setSuggestions(prev => prev.filter(s => s.id !== id));
      setSelectedSuggestion(null);
      showSuccess('Suggestion deleted successfully');
    } catch (error) {
      console.error('Error deleting suggestion:', error);
      showSuccess('Failed to delete suggestion');
    }
  };

  const sendMessageToUser = async (userEmail: string, message: string) => {
    try {
      const { Database } = await import('@/database/Database');
      const db = new Database();
      
      // Find user by email
      const userResult = await db.query(
        'SELECT id, email, name FROM users WHERE email = ?',
        [userEmail]
      );
      
      if (!userResult || userResult.length === 0) {
        showSuccess('User not found in system');
        return false;
      }
      
      const user = userResult[0];
      
      // Get or create system admin user
      const adminResult = await db.query(
        'SELECT id FROM users WHERE email = ?',
        ['system@whipt.app']
      );
      
      let adminUserId;
      if (adminResult && adminResult.length > 0) {
        adminUserId = adminResult[0].id;
      } else {
        // Create system admin user with all required fields (system account, never used for login)
        const adminId = `admin_${Date.now()}`;
        const timestamp = new Date().toISOString();
        await db.query(
          'INSERT INTO users (id, email, name, password_hash, created_at, last_active_at) VALUES (?, ?, ?, ?, ?, ?)',
          [adminId, 'system@whipt.app', 'WHIPT Admin', 'SYSTEM_ACCOUNT_NO_PASSWORD', timestamp, timestamp]
        );
        adminUserId = adminId;
      }
      
      // Insert admin notification message
      await db.query(
        `INSERT INTO messages (
          from_user_id, 
          to_user_id, 
          message, 
          created_at, 
          is_admin_notification,
          notification_type,
          read_status
        ) VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [
          adminUserId,
          user.id,
          message,
          new Date().toISOString(),
          1, // Mark as admin notification
          'suggestion_reply',
          0 // Unread
        ]
      );
      
      showSuccess('Message sent to user!');
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      showSuccess('Failed to send message');
      return false;
    }
  };

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('whipt_admin_auth', 'true');
      setPassword('');
    } else {
      alert('Incorrect password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('whipt_admin_auth');
    setPassword('');
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Music Management
  const addSong = () => {
    const newSong: Song = {
      id: `song_${Date.now()}`,
      title: 'New Song',
      artist: 'Artist Name',
      url: '',
      cover: 'https://via.placeholder.com/300x300/6366f1/ffffff?text=Cover'
    };
    setEditingSong(newSong);
  };

  const saveSong = async () => {
    if (!editingSong) return;
    
    setLoading(true);
    try {
      const existingIndex = songs.findIndex(s => s.id === editingSong.id);
      
      if (existingIndex >= 0) {
        // Update existing song
        await AdminAPI.updateSong(editingSong);
        setSongs(songs.map(s => s.id === editingSong.id ? editingSong : s));
      } else {
        // Create new song
        await AdminAPI.createSong(editingSong);
        setSongs([editingSong, ...songs]);
      }
      
      showSuccess('Song saved successfully!');
      setEditingSong(null);
    } catch (error) {
      console.error('Failed to save song:', error);
      showSuccess('Failed to save song');
    } finally {
      setLoading(false);
    }
  };

  const deleteSong = async (id: string) => {
    if (!confirm('Delete this song?')) return;
    
    setLoading(true);
    try {
      await AdminAPI.deleteSong(id);
      setSongs(songs.filter(s => s.id !== id));
      showSuccess('Song deleted successfully!');
    } catch (error) {
      console.error('Failed to delete song:', error);
      showSuccess('Failed to delete song');
    } finally {
      setLoading(false);
    }
  };

  // Ads Management
  const addAd = () => {
    const newAd: Ad = {
      id: `ad-${Date.now()}`,
      title: 'New Advertisement',
      description: 'Ad description here',
      image: '',
      link: 'https://example.com',
      active: true
    };
    setEditingAd(newAd);
  };

  const saveAd = async () => {
    if (!editingAd) return;
    
    setLoading(true);
    try {
      const existingIndex = ads.findIndex((a) => a.id === editingAd.id);
      const payload: AdminAPI.Advertisement = {
        ...editingAd,
        active: editingAd.active ? 1 : 0
      };

      if (existingIndex >= 0) {
        await AdminAPI.updateAd(payload);
        setAds(ads.map((ad) => (ad.id === editingAd.id ? editingAd : ad)));
      } else {
        await AdminAPI.createAd(payload);
        setAds([editingAd, ...ads]);
      }

      showSuccess('Advertisement saved successfully!');
      setEditingAd(null);
    } catch (error) {
      console.error('Failed to save ad:', error);
      showSuccess('Failed to save advertisement');
    } finally {
      setLoading(false);
    }
  };

  const deleteAd = async (id: string) => {
    if (!confirm('Delete this advertisement?')) return;
    
    setLoading(true);
    try {
      await AdminAPI.deleteAd(id);
      setAds(ads.filter(a => a.id !== id));
      showSuccess('Advertisement deleted successfully!');
    } catch (error) {
      console.error('Failed to delete ad:', error);
      showSuccess('Failed to delete advertisement');
    } finally {
      setLoading(false);
    }
  };

  const toggleAdActive = async (id: string) => {
    setLoading(true);
    try {
      await AdminAPI.toggleAdActive(id);
      setAds(ads.map(ad => 
        ad.id === id ? { ...ad, active: !ad.active } : ad
      ));
      
      showSuccess('Advertisement status updated!');
    } catch (error) {
      console.error('Failed to toggle ad:', error);
      showSuccess('Failed to update advertisement status');
    } finally {
      setLoading(false);
    }
  };

  // Toggle advertisements globally
  const toggleAdsGlobally = () => {
    const newState = !adsEnabled;
    setAdsEnabled(newState);
    AdminAPI.setAppSetting('ads_enabled', String(newState))
      .then(() => {
        showSuccess(`Advertisements ${newState ? 'ENABLED' : 'DISABLED'} globally!`);
      })
      .catch((error) => {
        console.error('Failed to update global ad setting:', error);
        setAdsEnabled(!newState);
        showSuccess('Failed to update advertisement setting');
      });
  };

  // User Management
  const deleteUser = async (userId: string, userName: string) => {
    // Protect admin account
    if (userName === 'Monica L. James' || userName.toLowerCase().includes('monica')) {
      alert('Cannot delete admin account!');
      return;
    }
    
    if (!confirm(`Delete user "${userName}"?\n\nThis will permanently delete:\n- User account\n- All messages\n- All sessions\n- All user data\n\nThis action cannot be undone.`)) return;
    
    setLoading(true);
    console.log('[Admin UI] Starting deletion of user:', userId, userName);
    
    try {
      await AdminAPI.deleteUser(userId);
      console.log('[Admin UI] Deletion successful, reloading data...');
      
      // Remove from local state immediately for instant UI feedback
      setUsers(prev => prev.filter(u => u.id !== userId));
      
      // Reload all data to ensure sync with database
      await loadData();
      showSuccess(`User "${userName}" deleted successfully!`);
    } catch (error: any) {
      console.error('[Admin UI] Failed to delete user:', error);
      const errorMsg = error.message || error.toString() || 'Unknown error';
      alert(`Failed to delete user "${userName}":\n\n${errorMsg}\n\nCheck browser console for details.`);
      // Reload data even on error to ensure UI is in sync
      await loadData();
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'super' | 'admin' | 'user') => {
    setLoading(true);
    try {
      const { updateUserRole } = await import('@/lib/auth');
      const result = await updateUserRole(userId, newRole);
      
      if (result.success) {
        // Update local state without waiting for full reload
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
        showSuccess(`User role updated to ${newRole}!`);
      } else {
        alert(`Failed to update role: ${result.error}`);
      }
    } catch (error) {
      console.error('Failed to update user role:', error);
      alert('Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8 shadow-2xl">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-white mb-2">🔐 Admin Access</h1>
              <p className="text-slate-400">WHIPT Organizer Dashboard</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Admin Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    placeholder="Enter admin password"
                    className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleLogin}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:shadow-lg hover:shadow-amber-500/50 transition-all"
              >
                Login to Dashboard
              </button>

              <p className="text-xs text-slate-500 text-center mt-4">
                Default password: <code className="bg-slate-900 px-2 py-1 rounded">whipt2026admin</code>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/organizer')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-sm text-amber-100">WHIPT Organizer Management</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-20 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-2xl animate-slide-in">
          {successMessage}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                <UsersIcon className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{loading ? '...' : userCount}</p>
                <p className="text-sm text-slate-400">Total Users</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Music className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{loading ? '...' : songs.length}</p>
                <p className="text-sm text-slate-400">Songs in Playlist</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <Image className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">
                  {loading ? '...' : `${ads.filter(a => a.active).length}/${ads.length}`}
                </p>
                <p className="text-sm text-slate-400">Active Ads</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700">
          <button
            onClick={() => setActiveTab('music')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'music'
                ? 'text-amber-400 border-b-2 border-amber-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            🎵 Music Player
          </button>
          <button
            onClick={() => setActiveTab('ads')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'ads'
                ? 'text-amber-400 border-b-2 border-amber-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            📢 Advertisements
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'users'
                ? 'text-amber-400 border-b-2 border-amber-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            👥 Users
          </button>
          <button
            onClick={() => setActiveTab('payment')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'payment'
                ? 'text-amber-400 border-b-2 border-amber-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            💳 Payment & Pricing
          </button>
          <button
            onClick={() => setActiveTab('suggestions')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'suggestions'
                ? 'text-amber-400 border-b-2 border-amber-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            💡 Suggestions {suggestions.filter(s => !s.read).length > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full">
                {suggestions.filter(s => !s.read).length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('broadcast')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'broadcast'
                ? 'text-amber-400 border-b-2 border-amber-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            📢 Broadcast
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'sessions'
                ? 'text-amber-400 border-b-2 border-amber-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            🎥 Sessions
          </button>
          <button
            onClick={() => navigate('/admin/resources')}
            className="px-6 py-3 font-semibold text-slate-400 hover:text-white transition-all"
          >
            📊 Resources
          </button>
          <button
            onClick={() => navigate('/admin/backend-services')}
            className="px-6 py-3 font-semibold text-slate-400 hover:text-white transition-all"
          >
            ⚙️ Backend Services
          </button>
        </div>

        {/* Music Tab */}
        {activeTab === 'music' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Music Playlist</h2>
              <button
                onClick={addSong}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Song
              </button>
            </div>

            {/* Song Editor */}
            {editingSong && (
              <div className="bg-slate-800 rounded-xl p-6 border-2 border-amber-500">
                <h3 className="text-xl font-bold mb-4">
                  {songs.find(s => s.id === editingSong.id) ? 'Edit Song' : 'Add New Song'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Song Title</label>
                    <input
                      type="text"
                      value={editingSong.title}
                      onChange={(e) => setEditingSong({ ...editingSong, title: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Artist Name</label>
                    <input
                      type="text"
                      value={editingSong.artist}
                      onChange={(e) => setEditingSong({ ...editingSong, artist: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Audio Source</label>
                    
                    {/* Toggle between URL and Embed Code */}
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setAudioInputType('url')}
                        className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${
                          audioInputType === 'url'
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        Audio URL
                      </button>
                      <button
                        type="button"
                        onClick={() => setAudioInputType('embed')}
                        className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${
                          audioInputType === 'embed'
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        Embed Code (iHeartRadio, Spotify, etc.)
                      </button>
                    </div>

                    {audioInputType === 'url' ? (
                      <input
                        type="url"
                        value={editingSong.url}
                        onChange={(e) => setEditingSong({ ...editingSong, url: e.target.value })}
                        placeholder="https://example.com/song.mp3 or https://soundcloud.com/..."
                        className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    ) : (
                      <>
                        <textarea
                          value={editingSong.url}
                          onChange={(e) => setEditingSong({ ...editingSong, url: e.target.value })}
                          placeholder='<iframe src="https://www.iheart.com/embed/..." ...></iframe>'
                          rows={4}
                          className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono text-xs"
                        />
                        <p className="text-xs text-slate-400 mt-2">
                          💡 Paste embed code from iHeartRadio, Spotify, Apple Music, etc.
                        </p>
                      </>
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Cover Image URL</label>
                    <input
                      type="url"
                      value={editingSong.cover}
                      onChange={(e) => setEditingSong({ ...editingSong, cover: e.target.value })}
                      placeholder="https://example.com/cover.jpg"
                      className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setEditingSong(null)}
                    className="flex-1 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveSong}
                    className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Save Song
                  </button>
                </div>
              </div>
            )}

            {/* Songs List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {songs.map((song) => (
                <div key={song.id} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                  <img
                    src={song.cover}
                    alt={song.title}
                    className="w-full h-40 object-cover rounded-lg mb-3"
                  />
                  <h3 className="font-bold text-white mb-1">{song.title}</h3>
                  <p className="text-sm text-slate-400 mb-3">{song.artist}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingSong(song)}
                      className="flex-1 px-3 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteSong(song.id)}
                      className="px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ads Tab */}
        {activeTab === 'ads' && (
          <div className="space-y-6">
            {/* Global Advertisement Toggle */}
            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-500/50 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-amber-400 mb-2">Global Advertisement Control</h3>
                  <p className="text-slate-300 text-sm">
                    {adsEnabled 
                      ? '✅ Advertisements are currently ENABLED for all users' 
                      : '❌ Advertisements are currently DISABLED for all users'}
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    This controls whether the advertisement banner appears on organizer pages
                  </p>
                </div>
                <button
                  onClick={toggleAdsGlobally}
                  className={`relative w-16 h-8 rounded-full transition-colors ${
                    adsEnabled ? 'bg-amber-500' : 'bg-slate-700'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                      adsEnabled ? 'translate-x-9' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Advertisements</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Manage all advertisements shown on organizer pages. Edit or delete existing ads below.
                </p>
              </div>
              <button
                onClick={addAd}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Advertisement
              </button>
            </div>

            {/* Ad Editor */}
            {editingAd && (
              <div className="bg-slate-800 rounded-xl p-6 border-2 border-amber-500">
                <h3 className="text-xl font-bold mb-4">
                  {ads.find(a => a.id === editingAd.id) ? 'Edit Advertisement' : 'Add New Advertisement'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Ad Title</label>
                    <input
                      type="text"
                      value={editingAd.title}
                      onChange={(e) => setEditingAd({ ...editingAd, title: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Description</label>
                    <textarea
                      value={editingAd.description}
                      onChange={(e) => setEditingAd({ ...editingAd, description: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Image URL</label>
                    <input
                      type="url"
                      value={editingAd.image}
                      onChange={(e) => setEditingAd({ ...editingAd, image: e.target.value })}
                      placeholder="https://example.com/ad-image.jpg"
                      className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Link URL</label>
                    <input
                      type="url"
                      value={editingAd.link}
                      onChange={(e) => setEditingAd({ ...editingAd, link: e.target.value })}
                      placeholder="https://example.com/product"
                      className="w-full px-4 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setEditingAd(null)}
                    className="flex-1 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveAd}
                    className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Save Advertisement
                  </button>
                </div>
              </div>
            )}

            {/* Ads List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ads.map((ad) => (
                <div key={ad.id} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                  <img
                    src={ad.image}
                    alt={ad.title}
                    className="w-full h-40 object-cover rounded-lg mb-3"
                  />
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-bold text-white">{ad.title}</h3>
                      <p className="text-sm text-slate-400">{ad.description}</p>
                    </div>
                    <button
                      onClick={() => toggleAdActive(ad.id)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 transition-all ${
                        ad.active
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                      title={ad.active ? 'Click to hide ad' : 'Click to show ad'}
                    >
                      {ad.active ? (
                        <>
                          <Eye className="w-3 h-3" />
                          <span>Active</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3 h-3" />
                          <span>Inactive</span>
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mb-3 truncate">{ad.link}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingAd(ad)}
                      className="flex-1 px-3 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteAd(ad.id)}
                      className="px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Beta Notice - Free Access During Beta */}
            <div className="bg-green-500/10 border-2 border-green-500/50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-green-300 mb-2">🎉 Beta Period - Free Access for All Users</h3>
                  <p className="text-sm text-green-200/80 mb-3">
                    <strong>NO PAYMENTS DURING BETA:</strong> All users currently have free access to both Student and Personal Organizers. 
                    No donations or subscriptions are being collected during the beta testing phase.
                  </p>
                  <div className="bg-green-500/20 rounded-lg p-4 space-y-3">
                    <div>
                      <strong className="text-green-300 text-sm">After Beta Test Ends:</strong>
                      <ol className="list-decimal list-inside mt-2 space-y-2 ml-2 text-xs text-green-200/90">
                        <li><strong>All accounts disabled</strong> - Users will be logged out and cannot access organizers</li>
                        <li><strong>Payment required to reactivate</strong> - Users must choose ONE of the following:
                          <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                            <li><strong>Option A: One-Time Donation</strong> - BBQ Community Donation ($4.99) for lifetime access to both organizers (single user)</li>
                            <li><strong>Option B: Individual Subscription</strong> - For single user:
                              <ul className="list-circle list-inside ml-6 mt-1">
                                <li>Student Organizer Only: $4.99/month or $49.99/year</li>
                                <li>Personal Organizer Only: $4.99/month or $49.99/year</li>
                                <li>Both Organizers (single user): $7.99/month or $79.99/year</li>
                              </ul>
                            </li>
                            <li><strong>Option C: Family/Household Bundle</strong> - For multiple users in same household:
                              <ul className="list-circle list-inside ml-6 mt-1">
                                <li>2 Users (any mix): $12.99/month or $129.99/year (save 20%)</li>
                                <li>3 Users (any mix): $17.99/month or $179.99/year (save 25%)</li>
                                <li>4 Users (any mix): $21.99/month or $219.99/year (save 30%)</li>
                                <li>5+ Users: Contact for custom family plan</li>
                              </ul>
                            </li>
                          </ul>
                          <p className="text-xs text-green-200/70 mt-2 ml-6 italic">
                            * Family bundles allow any combination: Adult with Personal + Kids with Student, Multiple students, etc.
                            <br />* Each user gets their own account with appropriate organizer access
                          </p>
                        </li>
                        <li><strong>Payment processed via Stripe</strong> - Account reactivated immediately after payment</li>
                        <li><strong>Email notification sent</strong> - Beta testers receive thank you message + payment instructions</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Cards - Disabled During Beta */}
            {false && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 opacity-50 pointer-events-none">
              <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <UsersIcon className="w-8 h-8 text-white/80" />
                  <span className="text-3xl font-bold text-white">{userCount}</span>
                </div>
                <p className="text-white/80 text-sm font-semibold">Total Users</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-8 h-8 text-white/80" />
                  <span className="text-3xl font-bold text-white">
                    {users.filter(u => u.student_organizer_paid).length}
                  </span>
                </div>
                <p className="text-white/80 text-sm font-semibold">Student Subscribers</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-8 h-8 text-white/80" />
                  <span className="text-3xl font-bold text-white">
                    {users.filter(u => u.personal_organizer_paid).length}
                  </span>
                </div>
                <p className="text-white/80 text-sm font-semibold">Personal Subscribers</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-8 h-8 text-white/80" />
                  <span className="text-3xl font-bold text-white">
                    {users.filter(u => u.student_organizer_paid || u.personal_organizer_paid).length}
                  </span>
                </div>
                <p className="text-white/80 text-sm font-semibold">Total Paid Users</p>
              </div>
            </div>
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Registered Users</h2>
              <div className="text-sm text-slate-400">
                Total: <span className="text-white font-bold">{userCount}</span> users
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                <p className="mt-4 text-slate-400">Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="bg-slate-800 rounded-xl p-12 text-center border border-slate-700">
                <UsersIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No users registered yet</p>
              </div>
            ) : (
              <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-900/50 border-b border-slate-700">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Demographics
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Organizer / Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Joined
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Last Login
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-slate-700/30 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-semibold text-white">{user.name}</p>
                                <p className="text-xs text-slate-500 font-mono break-all">ID: {user.id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-300">{user.email}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={user.role || 'user'}
                              onChange={(e) => handleRoleChange(user.id, e.target.value as 'super' | 'admin' | 'user')}
                              disabled={loading}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border-2 ${
                                user.role === 'super'
                                  ? 'bg-red-500/20 text-red-300 border-red-500/50 hover:bg-red-500/30'
                                  : user.role === 'admin'
                                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/50 hover:bg-amber-500/30'
                                  : 'bg-slate-700 text-slate-300 border-slate-600 hover:bg-slate-600'
                              } disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
                            >
                              <option value="user">👤 User</option>
                              <option value="admin">⚙️ Admin</option>
                              <option value="super">👑 Super Admin</option>
                            </select>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              {/* Age Group Badge */}
                              {user.demographics?.ageGroup ? (
                                <span className={`inline-flex w-fit items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                                  user.demographics.ageGroup === 'under13' 
                                    ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
                                    : user.demographics.ageGroup === 'teen'
                                    ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                                    : 'bg-green-500/20 text-green-300 border border-green-500/30'
                                }`}>
                                  {user.demographics.ageGroup === 'under13' && '🚸'}
                                  {user.demographics.ageGroup === 'teen' && '👦'}
                                  {user.demographics.ageGroup === 'adult' && '👤'}
                                  {user.demographics.ageGroup === 'under13' ? ' Under 13' : user.demographics.ageGroup === 'teen' ? ' Teen' : ' Adult'}
                                </span>
                              ) : (
                                <span className="inline-flex w-fit items-center px-2 py-1 rounded-full bg-slate-700 text-slate-400 text-xs">
                                  ? No age data
                                </span>
                              )}
                              
                              {/* Account Status */}
                              {user.demographics?.accountActive === false && (
                                <span className="inline-flex w-fit items-center gap-1 px-2 py-1 rounded-full bg-orange-500/20 text-orange-300 text-xs font-semibold border border-orange-500/30">
                                  🔒 Pending Consent
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1">
                              {/* Organizer Mode */}
                              {user.demographics?.organizerMode ? (
                                <span className={`inline-flex w-fit items-center px-2 py-1 rounded-full text-xs font-semibold ${
                                  user.demographics.organizerMode === 'student' 
                                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                                    : 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                }`}>
                                  {user.demographics.organizerMode === 'student' ? '🎓 Student' : '👤 Personal'}
                                </span>
                              ) : (
                                <span className="text-xs text-slate-500">Not selected</span>
                              )}
                              
                              {/* Payment/Beta Status */}
                              <div className="flex flex-wrap gap-1">
                                {user.demographics?.isPaid && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 text-xs">
                                    <DollarSign className="w-3 h-3" />
                                    Paid
                                  </span>
                                )}
                                {user.demographics?.isDiscount && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs">
                                    💰 Discount
                                  </span>
                                )}
                                {user.demographics?.isBeta && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-xs">
                                    β Beta
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          {/* Subscription columns hidden during beta */}
                          {false && (
                            <>
                              <td className="px-6 py-4">
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    {user.student_organizer_paid ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-300 text-xs font-semibold">
                                        <DollarSign className="w-3 h-3" />
                                        Student ✓
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-700 text-slate-400 text-xs">
                                        Student
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {user.personal_organizer_paid ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-300 text-xs font-semibold">
                                        <DollarSign className="w-3 h-3" />
                                        Personal ✓
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-700 text-slate-400 text-xs">
                                        Personal
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                {user.organizer_mode ? (
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                                    user.organizer_mode === 'student' 
                                      ? 'bg-blue-500/20 text-blue-300' 
                                      : 'bg-purple-500/20 text-purple-300'
                                  }`}>
                                    {user.organizer_mode === 'student' ? '🎓 Student' : '👤 Personal'}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-500">Not selected</span>
                                )}
                              </td>
                            </>
                          )}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-300">{formatDate(user.created_at)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-slate-400">
                              {user.last_active_at ? formatDate(user.last_active_at) : 'Never'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => deleteUser(user.id, user.name)}
                              className="px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors inline-flex items-center gap-2"
                              disabled={loading}
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="text-sm font-semibold">Delete</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-sm text-blue-300 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                User data is stored in Turso database. All users who sign up are automatically saved.
              </p>
            </div>
          </div>
        )}

        {/* Payment & Pricing Tab */}
        {activeTab === 'payment' && (
          <div className="space-y-6">
            <AdminPaymentSettings />
            
            {/* Test Payment Flow Button */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">Test Payment Flow</h3>
              <p className="text-sm text-slate-300 mb-4">
                Test the complete signup and payment process with different age scenarios
              </p>
              <button
                onClick={() => navigate('/payment')}
                className="px-6 py-3 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold hover:shadow-lg transition-all"
              >
                Launch Payment Test
              </button>
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-2">
              <div>
                <h2 className="text-2xl font-bold text-white">Active Sessions (browser heartbeats)</h2>
                <p className="text-slate-400 text-sm">Shows sessions currently running in this environment. Heartbeat every 10s.</p>
              </div>
              <button
                onClick={terminateAllSessions}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" /> Terminate All
              </button>
            </div>

            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              <div className="grid grid-cols-4 gap-4 px-4 py-3 text-sm font-semibold text-slate-300 border-b border-slate-700">
                <span>Room</span>
                <span>Started</span>
                <span>Duration</span>
                <span className="text-right">Actions</span>
              </div>

              {sessions.length === 0 ? (
                <div className="px-4 py-6 text-slate-400 text-sm">No active sessions detected.</div>
              ) : (
                sessions.map((s) => (
                  <div key={s.roomId} className="grid grid-cols-4 gap-4 px-4 py-3 border-b border-slate-700 last:border-b-0 items-center text-sm text-slate-200">
                    <div>
                      <p className="font-semibold text-white">{s.roomTitle || s.roomId}</p>
                      <p className="text-xs text-slate-400">ID: {s.roomId}</p>
                    </div>
                    <div className="text-slate-300">{formatDate(new Date(s.startedAt).toISOString())}</div>
                    <div className="text-slate-200">{formatDuration(Date.now() - s.startedAt)}</div>
                    <div className="text-right">
                      <button
                        onClick={() => terminateSession(s.roomId)}
                        className="px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 hover:text-red-100 transition-colors inline-flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" /> Terminate
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Broadcast Tab - Navigate to dedicated page */}
        {activeTab === 'broadcast' && (
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-2">📢 User Notifications</h2>
              <p className="text-slate-400 mb-6">Send messages to users on the dedicated broadcast page</p>
              <button
                onClick={() => navigate('/admin/broadcast')}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all flex items-center gap-2 mx-auto"
              >
                <Send className="w-5 h-5" />
                Go to Broadcast Page
              </button>
            </div>
          </div>
        )}

        {/* Suggestions Tab */}
        {activeTab === 'suggestions' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-2xl font-bold text-white">User Suggestions</h2>
                <p className="text-slate-400">
                  {suggestions.filter(s => !s.read).length} unread • {suggestions.length} total
                </p>
              </div>
              <button
                onClick={loadSuggestions}
                disabled={suggestionsLoading}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${suggestionsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {suggestionsLoading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-slate-400">Loading suggestions...</p>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="text-center py-12 bg-slate-800/50 rounded-xl">
                <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No suggestions yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                {/* Suggestions List */}
                <div className="md:col-span-1 space-y-3 max-h-[600px] overflow-y-auto">
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      onClick={() => {
                        setSelectedSuggestion(suggestion);
                        if (!suggestion.read) {
                          markSuggestionAsRead(suggestion.id);
                        }
                      }}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        selectedSuggestion?.id === suggestion.id
                          ? 'bg-amber-500/20 border-2 border-amber-500'
                          : suggestion.read
                          ? 'bg-slate-800/50 border border-slate-700 hover:bg-slate-800'
                          : 'bg-amber-500/10 border-2 border-amber-500/50 hover:bg-amber-500/15'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-bold text-white">{suggestion.name}</h3>
                        {!suggestion.read && (
                          <span className="w-2 h-2 bg-amber-500 rounded-full" />
                        )}
                      </div>
                      <p className="text-sm text-slate-400 mb-2 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {suggestion.email}
                      </p>
                      <p className="text-sm text-slate-400 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {new Date(suggestion.timestamp).toLocaleString()}
                      </p>
                      <p className="text-sm text-slate-300 mt-2 line-clamp-2">
                        {suggestion.suggestion}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Suggestion Detail */}
                <div className="md:col-span-2">
                  {selectedSuggestion ? (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h2 className="text-2xl font-bold text-white mb-2">
                            {selectedSuggestion.name}
                          </h2>
                          <a
                            href={`mailto:${selectedSuggestion.email}`}
                            className="text-amber-400 hover:text-amber-300 flex items-center gap-2"
                          >
                            <Mail className="w-4 h-4" />
                            {selectedSuggestion.email}
                          </a>
                          <p className="text-slate-400 text-sm mt-2 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {new Date(selectedSuggestion.timestamp).toLocaleString()}
                          </p>
                        </div>
                        
                        <button
                          onClick={() => deleteSuggestion(selectedSuggestion.id)}
                          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>

                      <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700 mb-6">
                        <h3 className="text-lg font-bold text-white mb-3">Suggestion:</h3>
                        <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                          {selectedSuggestion.suggestion}
                        </p>
                      </div>

                      {/* Send Message Section */}
                      <div className="bg-slate-900/50 rounded-lg p-6 border border-amber-500/30 mb-6">
                        <h3 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2">
                          <MessageSquare className="w-5 h-5" />
                          Send In-App Message
                        </h3>
                        <p className="text-slate-400 text-sm mb-4">
                          Send a direct notification to the user's messaging inbox. They'll see it as an alert next time they log in.
                        </p>
                        <textarea
                          value={selectedSuggestion.replyMessage || ''}
                          onChange={(e) => {
                            setSelectedSuggestion({
                              ...selectedSuggestion,
                              replyMessage: e.target.value
                            });
                          }}
                          placeholder="Type your message to the user here..."
                          className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none"
                          rows={4}
                        />
                        <div className="flex gap-3 mt-4">
                          <button
                            onClick={async () => {
                              if (!selectedSuggestion.replyMessage?.trim()) {
                                showSuccess('Please enter a message');
                                return;
                              }
                              const success = await sendMessageToUser(
                                selectedSuggestion.email,
                                selectedSuggestion.replyMessage
                              );
                              if (success) {
                                // Clear message after sending
                                setSelectedSuggestion({
                                  ...selectedSuggestion,
                                  replyMessage: ''
                                });
                              }
                            }}
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-amber-500/50 transition-all flex items-center justify-center gap-2"
                          >
                            <MessageSquare className="w-5 h-5" />
                            Send Message
                          </button>
                          <a
                            href={`mailto:${selectedSuggestion.email}?subject=Re: Your WHIPT Suggestion`}
                            className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-all text-center flex items-center justify-center gap-2"
                          >
                            <Mail className="w-5 h-5" />
                            Email Instead
                          </a>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
                      <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">Select a suggestion to view details</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
